import { httpService } from '@flexo/general';
import { useCallback, useEffect, useState } from 'react';

const ENV = (import.meta as unknown as { env: Record<string, string | undefined> }).env;

function getPrivacyPolicyUrl(): string {
  if (ENV.VITE_LEGAL_BASE_URL) {
    return `${ENV.VITE_LEGAL_BASE_URL}/hive/customApi/v1/kwick/privacyPolicyTermsConditions`;
  }

  if (ENV.VITE_APP_USE_LOCAL_API === 'true' && ENV.VITE_APP_CUSTOM_BASE_URL) {
    return `${ENV.VITE_APP_CUSTOM_BASE_URL}/kwick/v1/privacyPolicyTermsConditions`;
  }

  return `${ENV.VITE_APP_BASE_URL}/custom/${ENV.VITE_APP_ORG}/v1/privacyPolicyTermsConditions`;
}

export interface UsePrivacyPolicyOptions {
  /** Whether to fetch automatically on mount */
  enabled?: boolean;
}

export interface UsePrivacyPolicyReturn<TPolicy = unknown> {
  /** Latest privacy policy payload or null on failure */
  policy: TPolicy | null;
  /** Loading state for the ongoing request */
  loading: boolean;
  /** Error object when the last request failed */
  error: Error | null;
  /** Manually trigger a refresh of the policy */
  refetch: () => Promise<void>;
}

/**
 * React hook to fetch and expose the Privacy Policy payload.
 * Returns the policy, loading and error states, and a refetch function.
 */
export function usePrivacyPolicy<TPolicy = unknown>(
  options: UsePrivacyPolicyOptions = {}
): UsePrivacyPolicyReturn<TPolicy> {
  const { enabled = true } = options;

  const [policy, setPolicy] = useState<TPolicy | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);



      const res = await httpService.get({
        url: getPrivacyPolicyUrl(),
      });


      if (res?.status === 200) {     

        setPolicy(res.data as TPolicy);
      } else {
        setPolicy(null);
        setError(new Error(res?.statusText || 'Failed to load privacy policy'));
      }
    } catch (err: unknown) {
      setPolicy(null);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchPolicy();
  }, [enabled, fetchPolicy]);

  return {
    policy,
    loading,
    error,
    refetch: fetchPolicy,
  };
}
